/**
 * WordPress dependencies
 */
import { privateApis as componentsPrivateApis, Button, Modal } from '@wordpress/components';
import { __, sprintf, _n } from '@wordpress/i18n';
import { useMemo, useState, useCallback } from '@wordpress/element';
import { useRegistry } from '@wordpress/data';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  DropdownMenuV2: DropdownMenu,
  DropdownMenuGroupV2: DropdownMenuGroup,
  DropdownMenuItemV2: DropdownMenuItem,
  DropdownMenuSeparatorV2: DropdownMenuSeparator
} = unlock(componentsPrivateApis);
export function useHasAPossibleBulkAction(actions, item) {
  return useMemo(() => {
    return actions.some(action => {
      return action.supportsBulk && (!action.isEligible || action.isEligible(item));
    });
  }, [actions, item]);
}
export function useSomeItemHasAPossibleBulkAction(actions, data) {
  return useMemo(() => {
    return data.some(item => {
      return actions.some(action => {
        return action.supportsBulk && (!action.isEligible || action.isEligible(item));
      });
    });
  }, [actions, data]);
}
function ActionWithModal({
  action,
  selectedItems,
  setActionWithModal,
  onMenuOpenChange
}) {
  const eligibleItems = useMemo(() => {
    return selectedItems.filter(item => !action.isEligible || action.isEligible(item));
  }, [action, selectedItems]);
  const {
    RenderModal,
    hideModalHeader
  } = action;
  const onCloseModal = useCallback(() => {
    setActionWithModal(undefined);
  }, [setActionWithModal]);
  const label = typeof action.label === 'string' ? action.label : action.label(selectedItems);
  return /*#__PURE__*/_jsx(Modal, {
    title: !hideModalHeader ? label : undefined,
    __experimentalHideHeader: !!hideModalHeader,
    onRequestClose: onCloseModal,
    overlayClassName: "dataviews-action-modal",
    children: /*#__PURE__*/_jsx(RenderModal, {
      items: eligibleItems,
      closeModal: onCloseModal,
      onActionPerformed: () => onMenuOpenChange(false)
    })
  });
}
function BulkActionItem({
  action,
  selectedItems,
  setActionWithModal
}) {
  const registry = useRegistry();
  const eligibleItems = useMemo(() => {
    return selectedItems.filter(item => !action.isEligible || action.isEligible(item));
  }, [action, selectedItems]);
  const shouldShowModal = ('RenderModal' in action);
  return /*#__PURE__*/_jsx(DropdownMenuItem, {
    disabled: eligibleItems.length === 0,
    hideOnClick: !shouldShowModal,
    onClick: async () => {
      if (shouldShowModal) {
        setActionWithModal(action);
      } else {
        action.callback(eligibleItems, {
          registry
        });
      }
    },
    suffix: eligibleItems.length > 0 ? eligibleItems.length : undefined,
    children: action.label
  }, action.id);
}
function ActionsMenuGroup({
  actions,
  selectedItems,
  setActionWithModal
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(DropdownMenuGroup, {
      children: actions.map(action => /*#__PURE__*/_jsx(BulkActionItem, {
        action: action,
        selectedItems: selectedItems,
        setActionWithModal: setActionWithModal
      }, action.id))
    }), /*#__PURE__*/_jsx(DropdownMenuSeparator, {})]
  });
}
export default function BulkActions({
  data,
  actions,
  selection,
  onSelectionChange,
  getItemId
}) {
  const bulkActions = useMemo(() => actions.filter(action => action.supportsBulk), [actions]);
  const [isMenuOpen, onMenuOpenChange] = useState(false);
  const [actionWithModal, setActionWithModal] = useState();
  const selectableItems = useMemo(() => {
    return data.filter(item => {
      return bulkActions.some(action => !action.isEligible || action.isEligible(item));
    });
  }, [data, bulkActions]);
  const numberSelectableItems = selectableItems.length;
  const selectedItems = useMemo(() => {
    return data.filter(item => selection.includes(getItemId(item)) && selectableItems.includes(item));
  }, [selection, data, getItemId, selectableItems]);
  const areAllSelected = selectedItems.length === numberSelectableItems;
  if (bulkActions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(DropdownMenu, {
      open: isMenuOpen,
      onOpenChange: onMenuOpenChange,
      label: __('Bulk actions'),
      style: {
        minWidth: '240px'
      },
      trigger: /*#__PURE__*/_jsx(Button, {
        className: "dataviews-bulk-edit-button",
        __next40pxDefaultSize: true,
        variant: "tertiary",
        size: "compact",
        children: selectedItems.length ? sprintf( /* translators: %d: Number of items. */
        _n('Edit %d item', 'Edit %d items', selectedItems.length), selectedItems.length) : __('Bulk edit')
      }),
      children: [/*#__PURE__*/_jsx(ActionsMenuGroup, {
        actions: bulkActions,
        setActionWithModal: setActionWithModal,
        selectedItems: selectedItems
      }), /*#__PURE__*/_jsxs(DropdownMenuGroup, {
        children: [/*#__PURE__*/_jsx(DropdownMenuItem, {
          disabled: areAllSelected,
          hideOnClick: false,
          onClick: () => {
            onSelectionChange(selectableItems.map(item => getItemId(item)));
          },
          suffix: numberSelectableItems,
          children: __('Select all')
        }), /*#__PURE__*/_jsx(DropdownMenuItem, {
          disabled: selection.length === 0,
          hideOnClick: false,
          onClick: () => {
            onSelectionChange([]);
          },
          children: __('Deselect')
        })]
      })]
    }), actionWithModal && /*#__PURE__*/_jsx(ActionWithModal, {
      action: actionWithModal,
      selectedItems: selectedItems,
      setActionWithModal: setActionWithModal,
      onMenuOpenChange: onMenuOpenChange
    })]
  });
}
//# sourceMappingURL=bulk-actions.js.map